import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useLocation } from '../context/LocationContext';
import { fetchMenuForLocation } from '../utils/apiTransform';
import MenuItemModal from '../components/MenuItemModal';
import './Menu.css';

const Menu = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { addToCart } = useCart();
  const { selectedLocation } = useLocation();

  // Fetch menu data from API whenever location changes
  useEffect(() => {
    // Don't fetch if no location is selected yet
    if (!selectedLocation) {
      setLoading(false);
      return;
    }

    const fetchMenu = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch menu for the selected location/store
        const menuData = await fetchMenuForLocation(selectedLocation);

        setMenuItems(menuData.menuItems);
        setCategories(menuData.categories);

        // Reset to 'All' category when location changes
        setSelectedCategory('All');
      } catch (err) {
        console.error('Error fetching menu:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, [selectedLocation]); // Re-fetch when location changes

  const filteredItems =
    selectedCategory === 'All'
      ? menuItems
      : menuItems.filter((item) => item.category === selectedCategory);

  const handleOpenModal = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const handleAddToCart = (item, quantity = 1) => {
    // Add item to cart with location information and quantity
    const itemWithLocation = {
      ...item,
      location: selectedLocation,
      quantity: quantity
    };

    // Add the item to cart (quantity times or once with the quantity)
    for (let i = 0; i < quantity; i++) {
      addToCart(itemWithLocation);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="menu-page">
        <div className="menu-banner">
          <img src="/cake.png" alt="Belle Journée Bakery" className="banner-image" />
        </div>
        <div className="empty-state">
          <p>Loading menu...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="menu-page">
        <div className="menu-banner">
          <img src="/cake.png" alt="Belle Journée Bakery" className="banner-image" />
        </div>
        <div className="empty-state">
          <p>Error loading menu: {error}</p>
          <p>Unable to load menu for {selectedLocation}. Please try again later.</p>
        </div>
      </div>
    );
  }

  // No location selected state
  if (!selectedLocation) {
    return (
      <div className="menu-page">
        <div className="menu-banner">
          <img src="/cake.png" alt="Belle Journée Bakery" className="banner-image" />
        </div>
        <div className="empty-state">
          <p>Please select a pickup location to view the menu.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="menu-page">
      <div className="menu-banner">
        <img src="/cake.png" alt="Belle Journée Bakery" className="banner-image" />
      </div>

      <div className="category-filter">
        <div className="category-scroll">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-btn ${
                selectedCategory === category ? 'active' : ''
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      <div className="menu-grid">
        {filteredItems.map((item) => (
          <div
            key={item.id}
            className="menu-item clickable"
            onClick={() => handleOpenModal(item)}
          >
            <div className="menu-item-image">
              <img src={item.image} alt={item.name} />
              <div className="category-tag">{item.category}</div>
            </div>
            <div className="menu-item-content">
              <h3>{item.name}</h3>
              {item.description && (
                <p className="description">{item.description}</p>
              )}
              <div className="menu-item-footer">
                <span className="price">
                  ${item.price.toFixed(2)}
                </span>
                <button
                  className="add-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToCart(item, 1);
                  }}
                  disabled={!selectedLocation}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && !loading && (
        <div className="empty-state">
          <p>No items found in this category.</p>
        </div>
      )}

      <MenuItemModal
        item={selectedItem}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onAddToCart={handleAddToCart}
      />
    </div>
  );
};

export default Menu;
